// for clients for coach
// user name click to go to client details
// redirect to '/clients/${params.row.id}?tab=notes' default to notes tab
// here 'params.row.id' is the client id
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';
import CustomNoRowsOverlay from '../Table/CustomNoRowsOverlay';
import tableColumns from './Columns';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import SearchInput from '../StyledUi/SearchInput';
import { ICoach, fetchCoachesRequest } from '../../redux/coach/coachSlice';

export interface PaginationModelChangeProps {
  pageSize: number;
  page: number;
}

interface CellClickProps {
  field: string;
  row: {
    id: string;
  };
}

export default function ApplicationsTable() {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredData, setFilteredData] = useState<ICoach[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const skip = currentPage * pageSize;

  const { coaches, totalCoachesCount } = useAppSelector((state) => state.coach || []);
  const fetchCoachesLoading = useAppSelector((state) => state.coach.coachesLoading);

  const { t } = useTranslation();
  // const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      fetchCoachesRequest({
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        nameText: searchTerm,
        skip,
        take: pageSize,
      })
    );
  }, [searchTerm, pageSize, currentPage]);

  useEffect(() => {
    if (Array.isArray(coaches)) {
      setFilteredData(coaches);
    }
  }, [coaches]);

  const handleOnPaginationModelChange = (event: PaginationModelChangeProps) => {
    setPageSize(event.pageSize);
    setCurrentPage(event.page);
  };

  const handleOnCellClick = (params: CellClickProps) => {
    if (params.field !== 'view' && params.field !== 'approve' && params.field !== 'reject') {
      // navigate(`/application/${params.row.id}`);
    }
  };

  const getRowClassName = () => 'no-border';
  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box display="flex" position="relative" px="24px" py="20px">
        <SearchInput
          type="text"
          placeholder={t('coachesTable.applicationsTabSearchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Box position="absolute" right="36px" top="32px">
          <img src="/assets/icons/search.svg" alt="search" />
        </Box>
      </Box>
      <Box
        flexGrow="1"
        width="100%"
        height="calc(100vh - 264px)"
        sx={{
          '&::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
          },
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
        }}
      >
        <DataGrid
          onPaginationModelChange={handleOnPaginationModelChange}
          onCellClick={handleOnCellClick}
          rows={filteredData}
          rowCount={totalCoachesCount}
          paginationMode="server"
          columns={tableColumns as GridColDef[]}
          getRowClassName={getRowClassName}
          columnVisibilityModel={{
            id: false,
          }}
          pageSizeOptions={[5, 10, 15]}
          disableRowSelectionOnClick
          columnHeaderHeight={44}
          loading={fetchCoachesLoading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          sx={{
            borderRight: 'none',
            borderLeft: 'none',
            borderBottom: 'none',
            borderColor: 'primary.light',
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              paddingLeft: '30px',
              borderColor: 'primary.light',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: (theme) => theme.palette.primary.main,
              fontWeight: 500,
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              paddingLeft: '20px',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderColor: 'primary.light',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'primary.light',
            },
            '& .MuiTablePagination-root button': {
              mt: '4px',
              color: '#6CA3AC' /* Change text color */,
              '&:disabled': {
                opacity: 0.5,
              },
            },
            '& .MuiTablePagination-input': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
              ml: 0,
              pl: 0,
            },
            '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
              color: '#7CAEBE',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            },
            '& .MuiTablePagination-displayedRows': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
            },
            '& .MuiDataGrid-virtualScroller': {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '20px',
                border: '3px solid #f5f5f5',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#888',
              },
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Box>
    </Box>
  );
}
